<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import { corDoNome, iniciaisDoNome } from 'dashboard/helper/avatar';
import { corDoCanal } from 'dashboard/helper/corDoCanal';
import FollowUpHeaderBadge from 'dashboard/components-next/FollowUp/FollowUpHeaderBadge.vue';
import MoreActions from './MoreActions.vue';
import LigarBotao from 'dashboard/components-next/telefone/LigarBotao.vue';
import TransferConversationModal from './TransferConversationModal.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useNomeDoAgente } from 'dashboard/composables/useNomeDoAgente';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useConversationSidebarTab } from 'dashboard/composables/useConversationSidebarTab';
import { usePainelDoContato } from 'dashboard/composables/usePainelDoContato';
import { useAdmin } from 'dashboard/composables/useAdmin';
import LiberarCongelamentoDialog from './LiberarCongelamentoDialog.vue';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { requestSidebarTab } = useConversationSidebarTab();

const toggleConversationSearch = () => {
  emitter.emit(BUS_EVENTS.TOGGLE_CONVERSATION_SEARCH);
};

const { t } = useI18n();
const { abrirPainelDoContato } = usePainelDoContato();

const openHistory = () => {
  requestSidebarTab('hist');
  abrirPainelDoContato();
};
const openAppointment = () => {
  emitter.emit(BUS_EVENTS.OPEN_APPOINTMENT_BOOKING);
};

const showTransferModal = ref(false);

const store = useStore();
const route = useRoute();
const router = useRouter();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

// Sinal estrutural de "é cliente" (LegalClient), ver app/models/legal_client.rb.
const isLegalClient = computed(
  () => !!currentContact.value?.custom_attributes?.is_legal_client
);

// advup/Lumos — indicador "IA ativa" + toggle manual por conversa (§5.2.1).
const isAiActive = computed(() => !!currentChat.value.ai_active);
const isAiPaused = computed(() => !!currentChat.value.ai_paused_at);
const { nomeDoAgente } = useNomeDoAgente();
const aiAssistantName = computed(
  () => currentChat.value.ai_assistant_name || nomeDoAgente.value
);
const aiPausedTooltip = computed(() => {
  if (!isAiPaused.value) return '';
  const by = currentChat.value.ai_paused_by?.name;
  const at = new Date(currentChat.value.ai_paused_at).toLocaleTimeString(
    'pt-BR',
    { hour: '2-digit', minute: '2-digit' }
  );
  return by
    ? t('CONVERSATION.HEADER.AI_PAUSED_TOOLTIP', { name: by, time: at })
    : t('CONVERSATION.HEADER.AI_PAUSED_TOOLTIP_NO_NAME', { time: at });
});

// advup/Lumos — botão direto no header (antes só dentro dos 3 pontinhos,
// pedido do Victor pra ficar mais fácil de achar, mesmo padrão já usado
// pro ícone de Agendamento).
const showAiPauseButton = computed(() => isAiActive.value || isAiPaused.value);
const aiPauseButtonTooltip = computed(() =>
  isAiPaused.value ? t('CONTACT_PANEL.RESUME_AI') : t('CONTACT_PANEL.PAUSE_AI')
);
const toggleAiPause = async () => {
  const wasPaused = isAiPaused.value;
  try {
    await store.dispatch('toggleAiPause', currentChat.value.id);
    const successKey = wasPaused
      ? 'CONTACT_PANEL.AI_RESUMED_SUCCESS'
      : 'CONTACT_PANEL.AI_PAUSED_SUCCESS';
    useAlert(t(successKey));
  } catch (error) {
    useAlert(t('CONTACT_PANEL.AI_PAUSE_TOGGLE_ERROR'));
  }
};

// advup — fatia 0A (R1, D5): a pessoa está em atendimento humano por segurança —
// a IA e as automações não falam com ela, em nenhuma conversa, até um
// administrador liberar (com motivo).
const { isAdmin } = useAdmin();
const contatoCongelado = computed(() => currentChat.value.contato_congelado);
const congeladoTooltip = computed(() => {
  if (!contatoCongelado.value) return '';
  const desde = new Date(contatoCongelado.value.desde).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  return t('CONVERSATION.HEADER.CONGELADO.DICA', { desde });
});
const liberarDialog = ref(null);
const abrirLiberacao = () => liberarDialog.value?.open();

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const corCanal = computed(
  () => corDoCanal(inbox.value) || 'rgb(var(--slate-9))'
);

const fotoFalhou = ref(false);
watch(
  () => currentContact.value?.thumbnail,
  () => {
    fotoFalhou.value = false;
  }
);

const isAppointmentsEnabled = computed(() => {
  const isFeatureEnabled = store.getters['accounts/isFeatureEnabledonAccount'];
  if (typeof isFeatureEnabled !== 'function') return false;
  return isFeatureEnabled(accountId.value, FEATURE_FLAGS.APPOINTMENTS_MODULE);
});

const assignee = computed(() => props.chat?.meta?.assignee);
const chatTeam = computed(() => props.chat?.meta?.team);
</script>

<template>
  <!-- UMA OU DUAS LINHAS PELA LARGURA DO CABEÇALHO, NÃO DA TELA (05/10/2026).
       Era `xl:` — a partir de 1280 de tela, uma linha. Com o painel do
       contato aberto, o cabeçalho tem ~480px numa tela de 1280 e as sete
       ações deixavam "M…" para o nome. Agora quem decide é a largura dele
       (`ch-raiz` é o container; `ch-barra` muda de forma). -->
  <div ref="conversationHeader" class="ch-raiz flex-1 w-full min-w-0">
    <div
      class="ch-barra flex flex-col gap-3 items-center justify-between w-full min-w-0 px-3 pt-3 pb-2"
    >
      <div
        class="ch-barra__quem flex items-center justify-start w-full max-w-full min-w-0"
      >
        <!-- Voltar para a lista (celular): só a seta, como nos mensageiros —
           o "‹ Anterior" escrito comia a largura do nome. -->
        <button
          v-if="showBackButton"
          v-tooltip.bottom="$t('GENERAL_SETTINGS.BACK')"
          type="button"
          class="advup-acao ch-voltar"
          :aria-label="$t('GENERAL_SETTINGS.BACK')"
          @click="router.push(backButtonUrl)"
        >
          <i class="i-lucide-chevron-left" />
        </button>

        <!-- O rosto: o mesmo do cartão da lista e do topo da ficha — degradê
           da marca e o canal num selinho na cor dele. -->
        <button
          type="button"
          class="ch-rosto"
          :aria-label="$t('CONVERSATION.HEADER.OPEN_CONTACT_PANEL')"
          @click="abrirPainelDoContato"
        >
          <span
            class="advup-avatar advup-avatar--m"
            :class="`advup-avatar--${corDoNome(currentContact.name)}`"
            aria-hidden="true"
          >
            {{ iniciaisDoNome(currentContact.name) }}
            <img
              v-if="currentContact.thumbnail && !fotoFalhou"
              :src="currentContact.thumbnail"
              alt=""
              @error="fotoFalhou = true"
            />
          </span>
          <span
            v-if="inbox?.id"
            class="ch-canal"
            :style="{ '--canal': corCanal }"
            aria-hidden="true"
          >
            <ChannelIcon :inbox="inbox" class="ch-canal__ico" />
          </span>
        </button>

        <!-- 🔴 NO CELULAR A FICHA DO CONTATO NÃO TINHA PORTA VISÍVEL (08/09).
           Regra 2.5 do padrão: nada de função escondida. -->
        <button
          type="button"
          class="ch-ficha sm:hidden"
          :aria-label="$t('CONVERSATION.HEADER.OPEN_CONTACT_PANEL')"
          @click="abrirPainelDoContato"
        >
          <span class="i-lucide-id-card size-[18px]" />
        </button>

        <div class="ch-id">
          <div class="ch-id__linha">
            <button type="button" class="ch-nome" @click="abrirPainelDoContato">
              {{ currentContact.name }}
            </button>
            <span
              v-if="isLegalClient"
              class="advup-selo-cliente advup-selo-cliente--p"
            >
              <span class="i-lucide-shield-check" />
              {{ t('CONTACT_PANEL.LEGAL_CLIENT_BADGE') }}
            </span>
            <span
              v-if="!isHMACVerified"
              v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
              class="ch-alerta"
              :aria-label="$t('CONVERSATION.UNVERIFIED_SESSION')"
            >
              <i class="i-lucide-triangle-alert" />
            </span>
          </div>

          <!-- O chip com o nome do canal saiu (Victor, 17/09/2026: "é
             dispensável, já temos o ícone no avatar"). -->
          <!-- A chave é a conversa (24/10/2026): ao trocar de conversa os chips entram de novo,
               e o cabeçalho acompanha o painel de mensagens, que assenta. -->
          <div :key="chat.id" class="ch-chips">
            <!-- Fatia 0A: o selo de crise vem PRIMEIRO. A linha corta o que não
               cabe; no celular, quem cede é o telefone (que está na ficha), nunca o
               aviso de que a pessoa está em atendimento humano. -->
            <span
              v-if="contatoCongelado"
              v-tooltip="congeladoTooltip"
              class="ch-chip ch-chip--rubi"
              :aria-label="congeladoTooltip"
            >
              <i class="i-lucide-shield-alert" aria-hidden="true" />
              {{ $t('CONVERSATION.HEADER.CONGELADO.SELO') }}
            </span>
            <!-- O telefone desceu para cá (05/10/2026): na linha do nome ele não
               cedia, e quem cortava era o nome — "Maria T…" com o painel
               aberto, "Mari…" no celular. Aqui ele tem espaço e não disputa. -->
            <span v-if="currentContact.phone_number" class="ch-fone">
              {{ currentContact.phone_number }}
            </span>
            <FollowUpHeaderBadge :conversation-id="chat.id" />
            <!-- IA ativa ↔ pausada troca com o chip velho saindo e o novo entrando no lugar
                 (24/10/2026), em vez de estalar. -->
            <Transition name="ch-chip" mode="out-in">
              <span v-if="isAiActive" class="ch-chip ch-chip--iris">
                <span class="ch-chip__vivo" aria-hidden="true" />
                <i class="i-lucide-bot" aria-hidden="true" />
                {{ aiAssistantName }}
              </span>
              <span
                v-else-if="isAiPaused"
                v-tooltip="aiPausedTooltip"
                class="ch-chip ch-chip--pausada"
              >
                <i class="i-lucide-bot-off" aria-hidden="true" />
                {{ aiAssistantName }}
              </span>
            </Transition>
            <span v-if="chatTeam" class="ch-chip">
              <i class="i-lucide-users-round" aria-hidden="true" />
              {{ chatTeam.name }}
            </span>
            <span v-if="assignee" :key="assignee.id" class="ch-chip">
              <span
                class="advup-avatar advup-avatar--p ch-chip__rosto"
                :class="`advup-avatar--${corDoNome(assignee.name)}`"
                aria-hidden="true"
              >
                {{ iniciaisDoNome(assignee.name) }}
              </span>
              {{ assignee.available_name || assignee.name }}
            </span>
            <span v-if="isSnoozed" class="ch-chip ch-chip--ambar">
              <i class="i-lucide-alarm-clock" aria-hidden="true" />
              {{ snoozedDisplayText }}
            </span>
          </div>
        </div>
      </div>
      <div
        class="ch-barra__acoes flex flex-row items-center justify-start flex-shrink-0 gap-2 w-full header-actions-wrap"
      >
        <SLACardLabel
          v-if="hasSlaPolicyId"
          :chat="chat"
          show-extended-info
          :parent-width="width"
          class="hidden md:flex"
        />
        <!-- advup: ligar pela telefonia nativa (só com ramal e telefone pronto) -->
        <LigarBotao
          :numero="currentContact.phone_number || ''"
          :nome="currentContact.name || ''"
        />
        <button
          v-tooltip="$t('CONVERSATION.MESSAGE_SEARCH.TOOLTIP')"
          type="button"
          class="advup-acao advup-acao--tingida advup-acao--t-azul"
          :aria-label="$t('CONVERSATION.MESSAGE_SEARCH.TOOLTIP')"
          @click="toggleConversationSearch"
        >
          <i class="i-lucide-search" />
        </button>
        <button
          v-tooltip="$t('CONVERSATION.HEADER_ACTIONS.HISTORY')"
          type="button"
          class="advup-acao advup-acao--tingida"
          :aria-label="$t('CONVERSATION.HEADER_ACTIONS.HISTORY')"
          @click="openHistory"
        >
          <i class="i-lucide-history" />
        </button>
        <button
          v-if="isAppointmentsEnabled"
          v-tooltip="$t('CONVERSATION.HEADER_ACTIONS.SCHEDULE')"
          type="button"
          class="advup-acao advup-acao--tingida advup-acao--t-ambar"
          :aria-label="$t('CONVERSATION.HEADER_ACTIONS.SCHEDULE')"
          @click="openAppointment"
        >
          <i class="i-lucide-calendar-clock" />
        </button>
        <button
          v-if="contatoCongelado && isAdmin"
          v-tooltip="$t('CONVERSATION.HEADER.CONGELADO.ACAO')"
          type="button"
          class="advup-acao advup-acao--tingida advup-acao--t-teal"
          :aria-label="$t('CONVERSATION.HEADER.CONGELADO.ACAO')"
          @click="abrirLiberacao"
        >
          <i class="i-lucide-shield-check" />
        </button>
        <button
          v-if="showAiPauseButton"
          v-tooltip="aiPauseButtonTooltip"
          type="button"
          class="advup-acao advup-acao--tingida advup-acao--t-iris"
          :aria-label="aiPauseButtonTooltip"
          @click="toggleAiPause"
        >
          <i :class="isAiPaused ? 'i-lucide-bot' : 'i-lucide-bot-off'" />
        </button>
        <button
          v-tooltip="$t('CONVERSATION.HEADER_ACTIONS.TRANSFER')"
          type="button"
          class="advup-acao advup-acao--tingida advup-acao--t-teal"
          :aria-label="$t('CONVERSATION.HEADER_ACTIONS.TRANSFER')"
          @click="showTransferModal = true"
        >
          <i class="i-lucide-arrow-left-right" />
        </button>
        <MoreActions :conversation-id="currentChat.id" />
      </div>
      <!-- Sai pelo mesmo caminho por onde entrou (24/10/2026, `_advup-modal.scss`); o
           Teleport mora aqui para o <Transition> alcançar a folha. -->
      <Teleport to="body">
        <Transition name="advup-saida">
          <TransferConversationModal
            v-if="showTransferModal"
            @close="showTransferModal = false"
          />
        </Transition>
      </Teleport>
      <LiberarCongelamentoDialog
        v-if="contatoCongelado && isAdmin"
        ref="liberarDialog"
        :conversation-id="currentChat.id"
        :contact-id="currentContact.id"
      />
    </div>
  </div>
</template>

<style scoped>
.ch-raiz {
  container-type: inline-size;
}

.ch-barra {
  /* Mínimo, não altura fixa: no toque os botões têm 44px e a fileira de
     ações passava 16px da barra, por baixo das mensagens (medido em 390). */
  min-height: 96px;
}

/* Uma linha quando cabe: as ações medem ~343px e a identidade ~252px
   (medido em 05/10/2026). Em 680px sobram ~300px para o nome, que encurta
   com reticências — e é a largura da coluna em 1440 com a ficha aberta. */
@container (min-width: 680px) {
  .ch-barra {
    flex-direction: row;
    min-height: 0;
    height: 48px;
  }

  .ch-barra__quem {
    flex: 1;
    width: auto;
  }

  .ch-barra__acoes {
    justify-content: flex-end;
    width: auto;
  }
}

/* ── A identidade no alto da conversa ─────────────────────────────────────
   Redesenhada em 16/09/2026 para ser a mesma do cartão da lista e do topo
   da ficha: rosto com o degradê da marca, canal num selinho, e os detalhes
   (canal, IA, time, responsável) como chips do produto. */
.ch-voltar {
  margin-inline-end: var(--esp-1);
}

.ch-rosto {
  position: relative;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  border-radius: var(--r-redondo);
  background: none;
  cursor: pointer;
  transition: scale var(--mov-2) var(--mov-curva-pulo);
}

.ch-rosto:hover {
  scale: 1.05;
}

.ch-rosto:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 2px;
}

.ch-canal {
  position: absolute;
  right: -3px;
  bottom: -3px;
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  border: 2px solid rgb(var(--solid-1));
  border-radius: var(--r-redondo);
  background: var(--canal);
  color: rgb(255 255 255);
}

.ch-canal__ico {
  width: 12px;
  height: 12px;
}

.ch-id {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--esp-1);
  min-width: 0;
  margin-inline-start: var(--esp-2);
  overflow: hidden;
}

.ch-id__linha {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  max-width: 100%;
  min-width: 0;
}

.ch-nome {
  overflow: hidden;
  min-width: 0;
  padding: 0;
  border: 0;
  background: none;
  font-size: var(--txt-corpo);
  font-weight: var(--peso-titulo);
  color: rgb(var(--slate-12));
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.ch-nome::first-letter {
  text-transform: uppercase;
}

.ch-nome:hover {
  color: rgb(var(--blue-11));
}

.ch-fone {
  flex-shrink: 0;
  font-size: var(--txt-nota);
  color: rgb(var(--slate-10));
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.ch-alerta {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  color: rgb(var(--amber-10));
}

.ch-alerta > i {
  width: var(--ico-1);
  height: var(--ico-1);
}

.ch-chips {
  display: flex;
  align-items: center;
  gap: var(--esp-1);
  max-width: 100%;
  overflow: hidden;
}

.ch-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  flex-shrink: 0;
  max-width: 11rem;
  height: 20px;
  padding: 0 var(--esp-2);
  overflow: hidden;
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-3));
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-11));
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ch-chip > i {
  flex-shrink: 0;
  width: 11px;
  height: 11px;
}

.ch-chip__rosto {
  width: 14px;
  height: 14px;
  margin-inline-start: calc(-1 * var(--esp-1));
}

.ch-chip--iris {
  background: rgb(var(--iris-3));
  color: rgb(var(--iris-11));
}

/* O CHIP QUE ENTRA (24/10/2026): todo chip chega com um pop curto — ao abrir a conversa, ao
   trocar de responsável, ao a IA assumir ou pausar. Sem quique: só aparece. O ponto verde da
   IA ativa é PARADO de propósito: nada pulsa sozinho na tela que a equipe olha o dia todo. */
.ch-chip {
  animation: ch-chip-entra var(--mov-3) var(--mov-curva) both;
}

@keyframes ch-chip-entra {
  from {
    opacity: 0;
    transform: translate3d(0, 3px, 0) scale(0.96);
  }
}

.ch-chip-leave-active {
  transition:
    opacity var(--mov-1) var(--mov-curva-saida),
    transform var(--mov-1) var(--mov-curva-saida);
}

.ch-chip-leave-to {
  opacity: 0;
  transform: scale(0.96);
}

.ch-chip__vivo {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: var(--r-redondo);
  background: rgb(var(--teal-9));
}

.ch-chip--pausada {
  color: rgb(var(--slate-10));
  text-decoration: line-through;
}

/* Fatia 0A: rubi é o que alarma — a pessoa está em atendimento humano por
   segurança. Mesmo par de tinta da `.ficha-pill--alerta`. */
.ch-chip--rubi {
  background: rgb(var(--ruby-3));
  color: rgb(var(--ruby-11));
}

.ch-chip--ambar {
  background: rgb(var(--amber-3));
  color: color-mix(in srgb, rgb(var(--amber-12)) 18%, rgb(var(--amber-11)));
}

@media (prefers-reduced-motion: reduce) {
  .ch-rosto {
    transition: none;
  }

  .ch-chip,
  .ch-chip-enter-active,
  .ch-chip-leave-active {
    animation: none;
    transition: none;
  }
}

/* As ações do alto usam `.advup-acao--tingida` (_advup-acao.scss): cor por
   SIGNIFICADO, ícone tingido em repouso e ladrilho aceso no hover — o mesmo
   vocabulário da caixa de resposta. Moravam aqui como `.ch-acao`. */

/* ── Celular ─────────────────────────────────────────────────────────────────
   Botão que abre a ficha do contato. Só existe abaixo de 640px: no desktop a
   ficha fica ao lado e um botão a mais seria ruído. 32px de alvo, que é o
   mínimo do padrão para toque. */
@media (max-width: 639px) {
  .ch-ficha {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    margin-inline-start: var(--esp-2);
    border-radius: var(--r-3);
    color: rgb(var(--slate-11));
    background: transparent;
  }

  .ch-ficha:active {
    background: rgb(var(--slate-3));
  }
}
</style>
